import { useSuspenseQuery } from '@tanstack/react-query';
import { PeriodTypeValue } from 'src/common/components/PeriodTypeSwitch/PeriodTypeSwitch';
import { rsimGraphsQueries } from '../queries/rsimGraphsQueries';
import { getRsimPerformanceData, PerformanceData } from '../api/rsimGraphsApi';
import { setMinimalPromiseTime } from 'src/common/utils/setMinimalPromiseTime';
import { PercentageAreaChartDataset } from 'src/common/components/Graphs/common/Graphs/PercentageAreaChart';
import { TFunction, useTranslation } from 'react-i18next';

type UseRsimPerformanceDataProps = {
  accountRef: string;
  periodType: PeriodTypeValue;
};

type MappedUsageData = {
  xAxisLabels: string[];
  datasets: PercentageAreaChartDataset[];
};

export const RED_AREA_COLOR = '#f67d85';
const RED_BORDER_COLOR = '#d2535c';
export const BLUE_AREA_COLOR = '#6fb7e1';
const BLUE_BORDER_COLOR = '#3791c5';
export const GREEN_AREA_COLOR = '#96db9e';
const GREEN_BORDER_COLOR = '#4b9256';

const calculatePercentage = (value: number, total: number) => {
  if (!value) {
    return 0;
  }
  return (value / total) * 100;
};

const mapData = (data: PerformanceData[], t: TFunction) => {
  const xAxisLabels: string[] = [];

  const datasets: PercentageAreaChartDataset[] = [
    {
      label: t('graphs.performanceChartLabels.disconnected'),
      data: [],
      actualDataValues: [],
      borderColor: RED_BORDER_COLOR,
      backgroundColor: RED_AREA_COLOR,
    },
    {
      label: t('graphs.performanceChartLabels.backup'),
      data: [],
      actualDataValues: [],
      borderColor: BLUE_BORDER_COLOR,
      backgroundColor: BLUE_AREA_COLOR,
    },
    {
      label: t('graphs.performanceChartLabels.primary'),
      data: [],
      actualDataValues: [],
      borderColor: GREEN_BORDER_COLOR,
      backgroundColor: GREEN_AREA_COLOR,
    },
  ];

  data.forEach((point) => {
    xAxisLabels.push(point.label);

    const { primary, backup, disconnected } = point.value;
    const total = primary + backup + disconnected;

    datasets[2].data.push(calculatePercentage(primary, total));
    datasets[2].actualDataValues.push(primary);

    datasets[1].data.push(calculatePercentage(backup, total));
    datasets[1].actualDataValues.push(backup);

    datasets[0].data.push(calculatePercentage(disconnected, total));
    datasets[0].actualDataValues.push(disconnected);
  });

  return { xAxisLabels, datasets };
};

export const useRSimPerformanceData = ({ accountRef, periodType }: UseRsimPerformanceDataProps) => {
  const { t } = useTranslation();

  const { data } = useSuspenseQuery<
    Awaited<ReturnType<typeof getRsimPerformanceData>>,
    Error,
    MappedUsageData
  >({
    queryKey: rsimGraphsQueries.perforance({
      accountRef,
      periodType,
    }),
    queryFn: () => setMinimalPromiseTime(getRsimPerformanceData({ accountRef, periodType })),
    select: (data) => mapData(data, t),
  });

  return data;
};
